/**
 * TextInput Component
 * 
 * A styled text input with label, optional description, and error state.
 * 
 * @param {string} label - Label text for the input
 * @param {string} value - Current value
 * @param {function} onChange - Callback when value changes
 * @param {string} placeholder - Placeholder text
 * @param {string} description - Optional description below label
 * @param {string} error - Error message (shows error state when provided)
 * @param {string} type - Input type: "text", "email", "password", "number" (default: "text")
 * @param {string} color - Accent color for focus: "blue", "rose", "amber", "indigo" (default: "indigo")
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} required - Whether the field is required
 * @param {string} name - Input name attribute
 * @param {string} id - Input id attribute
 */

const COLOR_CLASSES = {
  blue: "focus:border-blue-500 focus:ring-blue-500",
  rose: "focus:border-rose-500 focus:ring-rose-500",
  amber: "focus:border-amber-500 focus:ring-amber-500",
  indigo: "focus:border-indigo-500 focus:ring-indigo-500",
};

export default function TextInput({
  label,
  value,
  onChange,
  placeholder = "",
  description = null,
  error = null,
  type = "text",
  color = "indigo",
  disabled = false,
  required = false,
  name = "",
  id = "",
}) {
  const focusColor = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;
  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="space-y-1.5">
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}

      {/* Description */}
      {description && (
        <p className="text-gray-400 text-xs">{description}</p>
      )}

      {/* Input */}
      <input
        type={type}
        id={inputId}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`
          w-full px-3 py-2.5 rounded-lg border text-sm text-gray-900
          placeholder:text-gray-400
          transition-colors duration-200
          ${error
            ? "border-rose-500 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
            : `border-gray-300 ${focusColor} focus:ring-2 focus:ring-opacity-20`
          }
          ${disabled
            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
            : "bg-white"
          }
        `}
      />

      {/* Error Message */}
      {error && (
        <p className="text-rose-600 text-xs flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}